import * as _ from "lodash";
import { IBusinessObject } from "../PA.Core/PA.Core.IF.BO";
import { IMessageProvider, IProgressProvider, MessageType } from "../PA.Core/PA.Core.IF.Basic";
import { PAdbContext } from "../PA.Core/PAdbContext";
import { IdentityProvider } from "../PA.Core/Basic/Identity";
import { MessageProvider } from "../PA.Core/Basic/Messages";
import { ProgressProvider } from "../PA.Core/Basic/Progress";
import {
    WB_CompanyRow,
    WB_LoaderKeyMapRow,
    WB_LoaderMetricMapRow,
    WB_LoaderMetricTermRow,
    WB_LoaderRow,
    WB_MasterDataSetRow,
    WB_MetricDefinitionRow,
} from "../PA.Core/PAdbSchemaRows";
import { IwbLoader } from "../PA.Core/PAdbSchemaRowsIF";
import {
    esWB_Company,
    esWB_Loader,
    esWB_Loader_KeyMap,
    esWB_Loader_MetricMap,
    esWB_Loader_MetricTerm,
    esWB_MasterData_Set,
    esWB_Metric_Definition,
} from "../PA.Core/PAdbSchema";
import { ESDataStore } from "../PA.Core/lib/ESDataStore";
import { ESRecord } from "../PA.Core/lib/ESRecord";
import { CreateOptions, ESRecordResolver } from "../PA.Core/lib/ESRecordResolver";
import { ASync } from "../PA.Core/Basic/ASync";
import { IJobParam } from "../PA.Job.Core/PA.Job.IF.JobParam";
import { JobParamManager } from "../PA.Job.Core/JobParamManager";
import {
    IHaveLoaderKeyMaps,
    IHaveLoaderMetricMaps,
    IHaveLoaderMetricTerms,
} from "../PA.Loader.Core/PA.Loader.IF.Aspects";
import { BehaviorSubject } from "rxjs";

import { PAEnvironment } from "../PA.Core/PAEnvironment";
import { LoaderBOValidations } from "./LoaderBO.Validations";

export class LoaderBO implements IBusinessObject, IHaveLoaderKeyMaps, IHaveLoaderMetricMaps, IHaveLoaderMetricTerms {
    //<editor-fold desc = "Properties and Constructor">
    Identity: IdentityProvider;

    CreateOptions: any;

    LoadOptions: any;
    _bIsLoaded: boolean;
    _OnLoadComplete: Promise<IBusinessObject>;
    LoadProgressProvider: IProgressProvider;

    ValidateOptions: any;
    _bIsValidated: boolean;
    ValidateMessageProvider: IMessageProvider;
    ValidateProgressProvider: IProgressProvider;

    SaveOptions: any;
    _bIsSaved: boolean;
    SaveProgressProvider: IProgressProvider;

    DeleteOptions: any;
    _bIsDeleted: boolean;
    DeleteMessageProvider: IMessageProvider;
    DeleteProgressProvider: IProgressProvider;

    dbContext: PAdbContext;
    drWB_Loader: WB_LoaderRow;

    constructor(options?: any) {
        this.CreateOptions = options || {};

        if ("dbContext" in this.CreateOptions) this.dbContext = this.CreateOptions.dbContext;

        this.Identity = new IdentityProvider({ id: 0, name: "New Loader" });

        this.LoadOptions = {};
        this._bIsLoaded = false;
        this._OnLoadComplete = undefined;
        this.LoadProgressProvider = new ProgressProvider("Loading", this);

        this.ValidateOptions = {};
        this._bIsValidated = false;
        this.ValidateMessageProvider = new MessageProvider();
        this.ValidateProgressProvider = new ProgressProvider("Validating", this);

        this.SaveOptions = {};
        this._bIsSaved = false;
        this.SaveProgressProvider = new ProgressProvider("Saving", this);

        this.DeleteOptions = {};
        this._bIsDeleted = false;
        this.DeleteMessageProvider = new MessageProvider();
        this.DeleteProgressProvider = new ProgressProvider("Deleting", this);

        this.drWB_Loader = null;

        this.IHaveLoaderKeyMaps_init();
        this.IHaveLoaderMetricMaps_init();
        this.IHaveLoaderMetricTerms_init();
    }

    get WB_Loader(): WB_LoaderRow {
        return this.drWB_Loader;
    }

    //</editor-fold>

    //<editor-fold desc="IBusinessObject" operations>

    async LoadNew(options?: any): Promise<IBusinessObject> {
        const self = this;
        this.LoadProgressProvider.start("Creating Loader");
        let internalOnComplete = (self._OnLoadComplete = (async () => {
            try {
                self.LoadOptions = options || {};
                if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
                else self.dbContext = new PAdbContext();

                if (this.dbContext == null) {
                    let sError = "Unable to determine dbContext for new Loader";
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(sError);
                    return self;
                }

                //Create record using dbContext
                let newLoaderRow: WB_LoaderRow = <WB_LoaderRow>await self.dbContext.createRecordWithKey(esWB_Loader);

                newLoaderRow.setDefaults();
                self.drWB_Loader = newLoaderRow;
                //add this to all the BOs
                self.dbContext.add(esWB_Loader, newLoaderRow);
                self.Identity.setAll(
                    self.WB_Loader.cif.WB_LOADER_ID,
                    self.WB_Loader.cif.LOADER_ID,
                    self.WB_Loader.cif.LOADER_NAME
                );
                self.LoadProgressProvider.setName("Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")");
                self.Identity.listenToRecordUpdates(self.WB_Loader, "WB_LOADER_ID", "LOADER_ID", "LOADER_NAME");

                self.LoadProgressProvider.finish();
                self._bIsLoaded = true;
                self._OnLoadComplete = null;
                await self.applyCreateOptions();
                self.Identity.setAll(
                    self.WB_Loader.cif.WB_LOADER_ID,
                    self.WB_Loader.cif.LOADER_ID,
                    self.WB_Loader.cif.LOADER_NAME
                );

                return self;
            } catch (err) {
                self.LoadProgressProvider.exception(err);
                return self;
            }
        })());

        return internalOnComplete;
    }

    async applyCreateOptions() {
        const self = this;

        let sId = self.CreateOptions?.sId || self.LoadOptions?.sId;
        if (!_.isEmpty(sId)) self.WB_Loader.cif.LOADER_ID = sId;

        let sName = self.CreateOptions?.sName || self.LoadOptions?.sName;
        if (!_.isEmpty(sName)) self.WB_Loader.cif.LOADER_NAME = sName;

        // Set Master Data Set
        let wbMasterDataSet = self.CreateOptions?.wbMasterDataSetId || self.LoadOptions?.wbMasterDataSetId;
        if (_.isNumber(wbMasterDataSet)) {
            let mds: WB_MasterDataSetRow = <any>await self.dbContext.find(esWB_MasterData_Set, wbMasterDataSet);
            if (mds) {
                self.WB_Loader.WB_MasterDataSetRow = mds;
                self.WB_Loader.cif.WB_MDSET_ID = wbMasterDataSet;
            }
        }

        // Set Company
        let wbCompany = self.CreateOptions?.wbCompanyId || self.LoadOptions?.wbCompanyId;
        if (_.isNumber(wbMasterDataSet)) {
            let company: WB_CompanyRow = <any>await self.dbContext.find(esWB_Company, wbCompany);
            if (company) {
                self.WB_Loader.WB_CompanyRow = company;
                self.WB_Loader.cif.WB_COMPANY_ID = wbCompany;
            }
        }
    }

    Load(anId: number | number, options?: any): Promise<IBusinessObject> {
        const self = this;

        let recordResolver: ESRecordResolver;
        let recordResolverLoadPromise: Promise<void>;

        self.LoadProgressProvider.start("Loading (" + anId + ")");

        self._OnLoadComplete = new Promise<IBusinessObject>((resolve, reject) => {
            if (self.bIsLoaded) {
                let sError = "Loader already loaded";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            self.LoadOptions = options || {};
            if ("dbContext" in self.LoadOptions) self.dbContext = self.LoadOptions.dbContext;
            else if (self.dbContext == null) self.dbContext = new PAdbContext();

            if (this.dbContext == null) {
                let sError = "Unable to determine dbContext for new Loader";
                self._OnLoadComplete = null;
                self.LoadProgressProvider.exception(sError);
                reject(sError);
                return;
            }

            //Added for record resolver
            if ("RecordResolver" in self.LoadOptions) {
                recordResolver = (<any>self.LoadOptions).RecordResolver;
                recordResolverLoadPromise = ASync.resolve();
            } else {
                recordResolver = LoaderBO.createRecordResolver({ dbContext: self.dbContext });
                recordResolverLoadPromise = LoaderBO.setupRecordResolver(recordResolver, [anId]);
            }

            recordResolverLoadPromise
                .then(function () {
                    let aLoaderRow: any = recordResolver.get(esWB_Loader, anId);

                    if (_.isEmpty(aLoaderRow)) {
                        self._OnLoadComplete = null;
                        self.LoadProgressProvider.exception("Id not found.");
                        resolve(self);
                        return;
                    }

                    self.drWB_Loader = aLoaderRow;
                    self.Identity.setAll(
                        self.WB_Loader.cif.WB_LOADER_ID,
                        self.WB_Loader.cif.LOADER_ID,
                        self.WB_Loader.cif.LOADER_NAME
                    );
                    self.LoadProgressProvider.setName(
                        "Loading " + self.Identity.name + "(" + self.Identity.id_ui + ")"
                    );
                    self.Identity.listenToRecordUpdates(self.WB_Loader, "WB_LOADER_ID", "LOADER_ID", "LOADER_NAME");

                    self._bIsLoaded = true;
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.finish();
                    resolve(self);
                })
                .catch((err) => {
                    self._OnLoadComplete = null;
                    self.LoadProgressProvider.exception(err);
                    reject(err);
                    return;
                });
        });
        return this._OnLoadComplete;
    }

    get OnLoadComplete(): Promise<IBusinessObject> {
        return this._OnLoadComplete;
    }

    get bIsLoaded(): boolean {
        return this._bIsLoaded;
    }

    Validate(options?: any): Promise<IBusinessObject> {
        const self = this;
        self.ValidateMessageProvider.clearMessages();
        self.ValidateProgressProvider.start(
            "Validating " + self.WB_Loader.cif.LOADER_NAME + "(" + self.WB_Loader.cif.LOADER_ID + ")"
        );

        return (async () => {
            // Does no validations yet
            self.ValidateOptions = options || {};

            try {
                if (self.dbContext == undefined) throw "Unable to determine dbContext for to Validate Loader.";

                await LoaderBOValidations.Validation(self);

                if (self.ValidateMessageProvider.countMessages(MessageType.Error) > 0) {
                    self._bIsValidated = false;
                } else {
                    self._bIsValidated = true;
                }
                self.ValidateProgressProvider.finish();
            } catch (err) {
                self._bIsValidated = false;
                self.ValidateProgressProvider.exception(err);
            } finally {
                return self;
            }
        })();
    }

    get bIsValidated(): boolean {
        return this._bIsValidated;
    }

    Save(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(false, options);
    }

    SaveBatch(options?: any): Promise<IBusinessObject> {
        return this.SaveUsing(true, options);
    }

    private async SaveUsing(bBatch: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbLoader = <IwbLoader>self.drWB_Loader;
        this.SaveProgressProvider.start("Saving " + wbLoader.LOADER_NAME + "(" + wbLoader.LOADER_ID + ")");

        try {
            if (self.dbContext == null) {
                self._bIsSaved = false;
                const sError: string = "Unable to determine dbContext to Save Loader.";
                self.SaveProgressProvider.exception(sError);
                return self;
            }

            self.SaveOptions = options || {};

            let saveFunction: (
                esName: string,
                saveRecords: ESRecord[] | ESRecord,
                options?: any
            ) => Promise<ESDataStore>;
            if (bBatch) {
                saveFunction = self.dbContext.SaveBatch.bind(self.dbContext);
            } else {
                saveFunction = self.dbContext.Save.bind(self.dbContext);
            }

            await self.Validate({});
            if (!self.bIsValidated) {
                self._bIsSaved = false;
                self.SaveProgressProvider.finish();
                return self;
            }

            await saveFunction(esWB_Loader, self.WB_Loader, {});

            const tasks: Promise<any>[] = [];
            tasks.push(saveFunction(esWB_Loader_KeyMap, self.WB_Loader.WB_LoaderKeyMapRows, {}));
            tasks.push(saveFunction(esWB_Loader_MetricMap, self.WB_Loader.WB_LoaderMetricMapRows, {}));
            tasks.push(saveFunction(esWB_Loader_MetricTerm, _.flatMap(self.WB_Loader.WB_LoaderMetricMapRows, (a) => a.WB_LoaderMetricTermRows),{}));
            tasks.push(saveFunction(esWB_MasterData_Set, self.WB_Loader.WB_MasterDataSetRow, {}));
            tasks.push(saveFunction(esWB_Company, self.WB_Loader.WB_CompanyRow, {}));
            await Promise.all(tasks);

            if (!self.JobParam) {
                if (self.WB_Loader.isActive) {
                    await self.IJobParam_OnLoadComplete();
                }
            }
            if (self.JobParam) {
                self.JobParam.id_ui = wbLoader.LOADER_ID;
                self.JobParam.name = wbLoader.LOADER_NAME;
                await self.JobParam.Save(bBatch);
            }

            if (bBatch) {
                self.dbContext.addBatchCompleteListener(() => {
                    self._bIsSaved = true;
                });
            } else {
                self._bIsSaved = true;
            }
            self.SaveProgressProvider.finish();
        } catch (err) {
            self.SaveProgressProvider.exception(err);
        }

        return self;
    }

    get bIsSaved(): boolean {
        return this._bIsSaved;
    }

    Delete(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(true, options);
    }

    DeleteOnSave(options?: any): Promise<IBusinessObject> {
        return this.DeleteUsing(false, options);
    }

    private async DeleteUsing(bSaveNow: boolean, options?: any): Promise<IBusinessObject> {
        const self = this;
        const wbLoader = <IwbLoader>self.drWB_Loader;

        try {
            this.DeleteMessageProvider.clearMessages();
            this.DeleteProgressProvider.start("Deleting " + wbLoader.LOADER_NAME + "(" + wbLoader.LOADER_ID + ")");

            self.drWB_Loader.markDeleted();
            _.forEach(self.drWB_Loader.WB_LoaderKeyMapRows, (a) => a.markDeleted());
            _.forEach(self.drWB_Loader.WB_LoaderMetricMapRows, (a) => a.markDeleted());

            if (self.JobParam) {
                self.JobParam.Delete(bSaveNow);
            }

            if (bSaveNow) {
                await self.SaveUsing(false);
            }
            self._bIsDeleted = true;
            self.DeleteProgressProvider.finish();
        } catch (err) {
            this.DeleteProgressProvider.exception(err);
        }

        return self;
    }

    get bIsDeleted(): boolean {
        return this._bIsDeleted;
    }

    //</editor-fold>

    //<editor-fold desc="IHaveLoaderKeyMaps" operations>

    private _OnKeyMapsCollectionChanged: BehaviorSubject<LoaderBO> = undefined;

    IHaveLoaderKeyMaps_init() {
        this._OnKeyMapsCollectionChanged = new BehaviorSubject<LoaderBO>(this);
    }

    get OnKeyMapsCollectionChanged(): BehaviorSubject<LoaderBO> {
        return this._OnKeyMapsCollectionChanged;
    }

    addKeyMap(dimId: string | string[], opts?: any): Promise<WB_LoaderKeyMapRow[]> {
        const self = this;

        let arrExistingDimensionsIds = _.map(
            _.filter(self.WB_Loader.WB_LoaderKeyMapRows, (aRow) => aRow.isActive),
            (aRow) => aRow.cif.DIMENSION_ID
        );
        let arrAddDimensionsIds: string[] = [];

        if (_.isArray(dimId)) {
            arrAddDimensionsIds = dimId;
        } else {
            arrAddDimensionsIds = [dimId];
        }

        arrAddDimensionsIds = _.difference(arrAddDimensionsIds, arrExistingDimensionsIds);

        return new Promise<WB_LoaderKeyMapRow[]>((resolve, error) => {
            async function RunAddNewRecord(): Promise<WB_LoaderKeyMapRow[]> {
                let returnRows: WB_LoaderKeyMapRow[] = [];

                for (let aDimId of arrAddDimensionsIds) {
                    let newRow = <WB_LoaderKeyMapRow>await self.dbContext.createRecordWithKey(esWB_Loader_KeyMap);
                    newRow.setDefaults();
                    newRow.cif.WB_LOADER_ID = self.WB_Loader.cif.WB_LOADER_ID;
                    newRow.cif.DIMENSION_ID = aDimId;
                    self.dbContext.add(esWB_Loader_KeyMap, newRow);
                    returnRows.push(newRow);
                }

                return returnRows;
            }

            RunAddNewRecord()
                .then((results: WB_LoaderKeyMapRow[]) => {
                    self.OnKeyMapsCollectionChanged.next(self);
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeKeyMap(wbLoaderKeyMapId: number): Promise<void> {
        const self = this;

        _.forEach(
            _.filter(self.WB_Loader.WB_LoaderKeyMapRows, (aRow) => aRow.isActive),
            (aRow) => {
                if (aRow.cif.WB_LOADERKEYMAP_ID == wbLoaderKeyMapId) {
                    aRow.markDeleted();
                }
            }
        );

        self.OnKeyMapsCollectionChanged.next(self);

        return ASync.resolve();
    }

    //</editor-fold>

    //<editor-fold desc="IHaveLoaderMetrics" operations>

    private _OnMetricMapsCollectionChanged: BehaviorSubject<LoaderBO> = undefined;

    IHaveLoaderMetricMaps_init() {
        this._OnMetricMapsCollectionChanged = new BehaviorSubject<LoaderBO>(this);
    }

    get OnMetricMapsCollectionChanged(): BehaviorSubject<LoaderBO> {
        return this._OnMetricMapsCollectionChanged;
    }

    addMetricMap(wbMetricId: number | number[], opts?: any): Promise<WB_LoaderMetricMapRow[]> {
        const self = this;

        let arrExistingMetricsIds = _.map(
            _.filter(self.WB_Loader.WB_LoaderMetricMapRows, (aRow) => aRow.isActive),
            (aRow) => aRow.cif.WB_MTRDFN_ID
        );
        let arrAddMetricIds: number[] = [];

        if (_.isArray(wbMetricId)) {
            arrAddMetricIds = wbMetricId;
        } else {
            arrAddMetricIds = [wbMetricId];
        }

        arrAddMetricIds = _.difference(arrAddMetricIds, arrExistingMetricsIds);

        return new Promise<WB_LoaderMetricMapRow[]>((resolve, error) => {
            async function RunAddNewRecord(): Promise<WB_LoaderMetricMapRow[]> {
                let returnRows: WB_LoaderMetricMapRow[] = [];

                for (let aMetricId of arrAddMetricIds) {
                    let aMetricRow = <WB_MetricDefinitionRow>(
                        await self.dbContext.find(esWB_Metric_Definition, aMetricId)
                    );
                    if (_.isEmpty(aMetricRow)) {
                        continue;
                    }

                    let newRow = <WB_LoaderMetricMapRow>await self.dbContext.createRecordWithKey(esWB_Loader_MetricMap);
                    newRow.setDefaults();
                    newRow.cif.WB_LOADER_ID = self.WB_Loader.cif.WB_LOADER_ID;
                    newRow.cif.WB_MTRDFN_ID = aMetricId;
                    newRow.WB_MetricDefinitionRow = aMetricRow;
                    newRow.cif.METRIC_FIELDNAME = aMetricRow.cif.SUBJECTAREA_COLUMNNAME;
                    self.dbContext.add(esWB_Loader_MetricMap, newRow);
                    returnRows.push(newRow);
                }

                return returnRows;
            }

            RunAddNewRecord()
                .then((results: WB_LoaderMetricMapRow[]) => {
                    self.OnMetricMapsCollectionChanged.next(self);
                    resolve(results);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeMetricMap(wbLoaderMetricMapId: number): Promise<void> {
        const self = this;

        _.forEach(
            _.filter(self.WB_Loader.WB_LoaderMetricMapRows, (aRow) => aRow.isActive),
            (aRow) => {
                if (aRow.cif.WB_LOADERMETRICMAP_ID == wbLoaderMetricMapId) {
                    aRow.markDeleted();
                }
            }
        );

        self.OnMetricMapsCollectionChanged.next(self);

        return ASync.resolve();
    }

    get MetricDefinitions(): Promise<WB_MetricDefinitionRow[]> {
        const self = this;

        return new Promise<WB_MetricDefinitionRow[]>((resolve, error) => {
            switch (self.WB_Loader.cif.LOADERTARGET_TYPE) {
                case "SubjectArea":
                    let query: any = {
                        where: {
                            WB_SUBJECTAREA_ID: self.WB_Loader.cif.WB_LOADERTARGET_ID,
                        },
                    };
                    self.dbContext
                        .findAll(esWB_Metric_Definition, query)
                        .then((results: WB_MetricDefinitionRow[]) => {
                            resolve(results);
                        })
                        .catch((err) => {
                            error(err);
                        });
                    break;

                default:
                    resolve([]);
            }
        });
    }

    //</editor-fold>

    //<editor-fold desc="IHaveLoaderMetricTerms" operations>

    private _OnMetricTermsCollectionChanged: BehaviorSubject<LoaderBO>;

    IHaveLoaderMetricTerms_init() {
        this._OnMetricTermsCollectionChanged = new BehaviorSubject<LoaderBO>(this);
    }

    get OnMetricTermsCollectionChanged(): BehaviorSubject<LoaderBO> {
        return this._OnMetricTermsCollectionChanged;
    }

    addMetricTerm(wbLoaderMetricMapRow: WB_LoaderMetricMapRow, opts?: any): Promise<WB_LoaderMetricTermRow> {
        const self = this;

        return new Promise<WB_LoaderMetricTermRow>((resolve, error) => {
            if (_.isEmpty(wbLoaderMetricMapRow)) {
                resolve(undefined);
                return;
            }

            self.dbContext
                .createRecordWithKey(esWB_Loader_MetricTerm, {})
                .then((aNewRow: WB_LoaderMetricTermRow) => {
                    aNewRow.setDefaults();
                    aNewRow.cif.WB_LOADERMETRICMAP_ID = wbLoaderMetricMapRow.cif.WB_LOADERMETRICMAP_ID;
                    self.dbContext.add(esWB_Loader_MetricTerm, aNewRow);
                    self.OnMetricTermsCollectionChanged.next(self);
                    resolve(aNewRow);
                })
                .catch((err) => {
                    error(err);
                });
        });
    }

    removeMetricTerm(wbLoaderMetricMapRow: WB_LoaderMetricMapRow, wbLoaderMetricTermId: number): Promise<void> {
        const self = this;

        if (_.isEmpty(wbLoaderMetricMapRow)) {
            return ASync.resolve();
        }

        _.forEach(
            _.filter(wbLoaderMetricMapRow.WB_LoaderMetricTermRows, (aRow) => aRow.isActive),
            (aRow) => {
                if (aRow.cif.WB_LOADERMETRICTERM_ID == wbLoaderMetricTermId) {
                    aRow.markDeleted();
                }
            }
        );

        self.OnMetricTermsCollectionChanged.next(self);
        return ASync.resolve();
    }

    //</editor-fold>

    //<editor-fold desc = "Job Param">
    _jobParam: IJobParam;

    async IJobParam_OnLoadComplete(): Promise<void> {
        if (!_.isEmpty(this._jobParam)) {
            return;
        }
        const jpMgr = new JobParamManager();
        const wbLoader: IwbLoader = this.WB_Loader.cif;

        this._jobParam = await jpMgr.findOrCreate({
            dbContext: this.dbContext,
            JobSchedulerModuleId: "PnLLoader",

            sourceType: "Loader",
            sourceWbId: wbLoader.WB_LOADER_ID,

            appExecId: "Loader",
            wbTargetId: wbLoader.WB_LOADER_ID,

            wbMasterDataSetId: this.WB_Loader.WB_MasterDataSetRow.cif.WB_MDSET_ID,
            wbCompanyId: this.WB_Loader.WB_CompanyRow.cif.WB_COMPANY_ID,
            wbSalesOrgId: 0,

            JobParamId: wbLoader.LOADER_ID,
            JobParamName: wbLoader.LOADER_NAME,
            JobProcessorType: "PnLLoaderProcessor",
            DependentTriggerId: "PnLLoader",
        });
    }

    get JobParam(): IJobParam {
        return this._jobParam;
    }

    //</editor-fold>

    //<editor-fold desc="IRecordResolver" operations>

    static createRecordResolver(boLoadOptions: any): ESRecordResolver {
        let opts = boLoadOptions || {};
        let dbContext: PAdbContext;

        if ("dbContext" in opts) {
            dbContext = opts.dbContext;
        } else {
            dbContext = new PAdbContext({});
        }

        const loader_options: CreateOptions = {
            standardConditions: {},
            dctIncludeBelongsToNamedRelations: {},
        };

        const aRecResolver = new ESRecordResolver(dbContext, loader_options);

        return aRecResolver;
    }

    static setupRecordResolver(recordResolver: ESRecordResolver, ids: string[] | number[]): Promise<void> {
        return new Promise<void>((resolve, reject) => {
            recordResolver
                .findAllWithRelationships(esWB_Loader, ids)
                .then((result) => {
                    resolve();
                })
                .catch((error) => {
                    reject(error);
                });
        });
    }

    static concurrentLoadCountHint(): number {
        return 10;
    }

    //</editor-fold>
}
PAEnvironment.registerBusinessObjectClass(LoaderBO, "LoaderBO", "Loader");
